/**
 * RAT Programs — live list of rolled-out Risk Assessment programs.
 *
 * Provides a "Roll Out" entry point directly in the Programs tab so the user
 * does not need to switch to the RAT Templates tab. Pattern mirrors
 * GovernancePrograms (SOP walkthroughs).
 */
import React, { useCallback, useEffect, useState } from 'react';
// merged: incoming + existing
import { Search, Calendar, User, Rocket, Plus, Lock } from 'lucide-react';
import Skeleton from '../common/Skeleton';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import NoDataFound from '../common/NoDataFound';
import GeographyPicker, { GeographySelection, isGeographyComplete } from '../common/GeographyPicker';
import riskAssessmentProgramService, { RiskAssessmentProgram } from '../../services/riskAssessmentProgramService';
import riskAssessmentService, { RiskAssessmentTemplate } from '../../services/riskAssessmentService';
import { userService } from '../../services/userService';
import extractApiError from '../../utils/apiError';
import RiskAssessmentExecutionModal from './RiskAssessmentExecutionModal';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import { usePermissions, usePermission } from '../../contexts/PermissionContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ratStageBadgeClass } from '../../utils/statusVariant';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

const ACT_RAT_PROGRAM = 'RSK_RAT_PROGRAMS';
const WF_KIND = 'rsk_rat_program';

// Single source of truth for RAT stage badge colours (shared with the ITAT and
// Control Testing staff worklists via statusVariant).
const statusBadgeClass = ratStageBadgeClass;

// ----------------------------------------------------------------------------
// Roll Out modal — template picker + maker/checker/geography/dates.
// ----------------------------------------------------------------------------

const RATRolloutModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onRolledOut: () => void;
    onError: (msg: string) => void;
}> = ({ isOpen, onClose, onRolledOut, onError }) => {
    const [templates, setTemplates] = useState<RiskAssessmentTemplate[]>([]);
    const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [templateId, setTemplateId] = useState('');
    const [makerId, setMakerId] = useState('');
    const [checkerId, setCheckerId] = useState('');
    const [geography, setGeography] = useState<GeographySelection>({ mode: 'locations', location_ids: [] });
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const { errors, validateField, clearError, validateAll, reset } = useFieldValidation();

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoading(true);
        setTemplateId(''); setMakerId(''); setCheckerId('');
        setGeography({ mode: 'locations', location_ids: [] }); setStartDate(''); setEndDate('');
        reset();
        (async () => {
            try {
                const [tplList, userList] = await Promise.all([
                    riskAssessmentService.list(),
                    userService.getUsersDropdown(),
                ]);
                if (cancelled) return;
                // Only roll-out-ready templates: active, approved, and with at
                // least one risk in scope. Drafts/submitted/rejected templates
                // (grey/amber/red dots) or unconfigured ones (0 risks) would
                // fail server-side with "Template has no risks in scope".
                setTemplates((tplList || []).filter(t =>
                    (t.is_active ?? 1) === 1 &&
                    (t.workflow_state === 'approved' || t.is_approved === 1) &&
                    (t.risk_count ?? 0) > 0
                ));
                setUsers(userList.map(u => ({ id: u.id, name: u.name })));
            } catch (e: any) {
                if (!cancelled) onError(extractApiError(e, 'Failed to load options'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // Only reset/reload when the modal opens. `onError` is an inline
        // callback recreated on every parent render (e.g. when a toast fires),
        // so including it here would re-run this effect and wipe the user's
        // in-progress inputs whenever an error toast is shown.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        const valid = validateAll([
            { name: 'start_date', code: 305, value: startDate, required: true },
            { name: 'end_date', code: 305, value: endDate, required: true },
        ]);
        if (!valid) return;
        if (!templateId || !makerId || !checkerId || !startDate || !endDate || !isGeographyComplete(geography)) {
            onError('Template, maker, checker, geography, start date and end date are required.');
            return;
        }
        if (endDate < startDate) { onError('End date must be on or after start date.'); return; }
        setSaving(true);
        try {
            await riskAssessmentProgramService.rollout(parseInt(templateId, 10), {
                maker_id: parseInt(makerId, 10),
                checker_id: parseInt(checkerId, 10),
                geography,
                start_date: startDate,
                end_date: endDate,
            });
            onRolledOut();
            onClose();
        } catch (err: any) {
            onError(extractApiError(err, 'Failed to roll out program.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={false}>
            <Modal.Header>Roll Out RAT Program</Modal.Header>
            <Modal.Body>
                <form id="rat-rollout-form" onSubmit={submit} className="flex flex-col gap-y-4">
                    {makerId !== '' && makerId === checkerId && (
                        <div className="warning-banner" role="status"
                             data-testid="WRN_rollout_same_maker_checker">
                            Maker and checker are the same person. This assessment will be performed and approved by one individual — permitted, and recorded as self-reviewed.
                        </div>
                    )}
                    <FormControl.SearchSelect
                        label="RAT Template"
                        required
                        placeholder="Select template…"
                        options={templates.map(t => ({
                            value: t.id,
                            label: `${t.assessment_code ? `${t.assessment_code} — ` : ''}${t.risk_assessment_name}`,
                        }))}
                        value={templateId}
                        onChange={v => setTemplateId(v != null ? String(v) : '')}
                        disabled={loading}
                        clearable
                        data-testid="SEL_risk_programs_rollout_template"
                    />
                    <FormControl.SearchSelect
                        label="Maker (executor)"
                        required
                        placeholder="Select user…"
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        value={makerId}
                        onChange={v => setMakerId(v != null ? String(v) : '')}
                        disabled={loading}
                        clearable
                        data-testid="SEL_risk_programs_rollout_maker"
                    />
                    <FormControl.SearchSelect
                        label="Checker (approver)"
                        required
                        placeholder="Select user…"
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        value={checkerId}
                        onChange={v => setCheckerId(v != null ? String(v) : '')}
                        disabled={loading}
                        clearable
                        data-testid="SEL_risk_programs_rollout_checker"
                    />
                    <div>
                        <label className="fg__label">Geography <span className="fg__required" aria-hidden="true">*</span></label>
                        <GeographyPicker value={geography} onChange={setGeography} disabled={loading} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormControl.DatePicker
                            label="Start Date"
                            required
                            value={startDate}
                            onChange={(e) => { setStartDate(e.target.value); clearError('start_date'); }}
                            onBlur={() => validateField('start_date', 305, startDate, { required: true })}
                            error={errors['start_date']}
                            data-testid="INP_risk_programs_rollout_start"
                        />
                        <FormControl.DatePicker
                            label="End Date"
                            required
                            value={endDate}
                            onChange={(e) => { setEndDate(e.target.value); clearError('end_date'); }}
                            onBlur={() => validateField('end_date', 305, endDate, { required: true })}
                            error={errors['end_date']}
                            data-testid="INP_risk_programs_rollout_end"
                        />
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button type="submit" form="rat-rollout-form" variant="fill" icon={Rocket} disabled={saving || loading} data-testid="BTN_risk_programs_rollout_submit">
                    {saving ? 'Rolling out…' : 'Roll Out'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ----------------------------------------------------------------------------
// Main listing
// ----------------------------------------------------------------------------

const RiskPrograms: React.FC = () => {
    const { formatDate } = useEntitySettings();
    const [rows, setRows] = useState<RiskAssessmentProgram[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [openId, setOpenId] = useState<number | null>(null);
    const [isRolloutOpen, setIsRolloutOpen] = useState(false);
    const [workflowRow, setWorkflowRow] = useState<RiskAssessmentProgram | null>(null);
    const { check } = usePermissions();
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_RAT_PROGRAM, 'view');
    const canModify = check(ACT_RAT_PROGRAM, 'view+modify').allowed;
    const canApprove = check(ACT_RAT_PROGRAM, 'view+modify+approve').allowed;

    const refresh = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const data = await riskAssessmentProgramService.list({ search: search || undefined });
            setRows(data);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load programs') });
        } finally {
            setLoading(false);
        }
    }, [search, canView]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

    // Distinct statuses present in the loaded rows, for the filter dropdown.
    const statusOptions = Array.from(
        new Set(rows.map(p => p.status || 'Not-Started')),
    ).sort();

    const filtered = !statusFilter
        ? rows
        : rows.filter(p => (p.status || 'Not-Started') === statusFilter);

    const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to Risk Assessment Programs." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.SearchSelect
                        value={statusFilter}
                        onChange={v => setStatusFilter((v || '') as string)}
                        className="filter-select"
                        noSearchIcon
                        data-testid="SEL_risk_programs_status"
                        options={[
                            { value: '', label: 'All Statuses' },
                            ...statusOptions.map(s => ({ value: s, label: s })),
                        ]}
                    />
                    <FormControl.Input
                        type="text"
                        placeholder="Search programs…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_risk_programs_search"
                    />
                    {canModify && (
                        <Button variant="fill" icon={Plus} onClick={() => setIsRolloutOpen(true)} data-testid="BTN_risk_programs_rollout">Roll Out</Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        {/* Workflow status dot hidden — STATUS column conveys state.
                                        <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                                        */}
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Program</th>
                                        <th className="table-list-th">Template</th>
                                        <th className="table-list-th">Geography</th>
                                        <th className="table-list-th">Window</th>
                                        <th className="table-list-th">Maker</th>
                                        <th className="table-list-th">Checker</th>
                                        <th className="table-list-th table-list-th--right">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'sm', 'pill', 'xs', 'xs', { width: 'pill', align: 'right' }]} rows={4} />}
                                    {!loading && paged.length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="table-list-cell">
                                                <NoDataFound
                                                    title={search || statusFilter ? 'No Matching Programs' : 'No RAT Programs Yet'}
                                                    message={search || statusFilter ? 'No programs match your filters.' : 'Click "Roll Out" to launch one from a RAT Template.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paged.map((p, idx) => (
                                        <tr
                                            key={p.id}
                                            className="table-list-row"
                                            onClick={() => setOpenId(p.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {/* Workflow status dot hidden — STATUS column conveys state.
                                            <td
                                                className="table-list-cell table-list-cell--dot"
                                                onClick={(e) => { e.stopPropagation(); setWorkflowRow(p); }}
                                                title="Workflow — view timeline & act"
                                                data-testid={`BTN_risk_programs_row_${p.id}_workflow`}
                                            >
                                                <ApprovalDot
                                                    state={(((p as any).workflow_state) || 'draft') as WorkflowState}
                                                    onClick={() => setWorkflowRow(p)}
                                                    title="Workflow — view timeline & act"
                                                />
                                            </td>
                                            */}
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title cell-name-lock">
                                                    {(p as any).workflow_state === 'approved' && (
                                                        <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                                    )}
                                                    {p.program_name}
                                                </span>
                                                {p.program_code && (
                                                    <span className="table-list-cell-sub">{p.program_code}</span>
                                                )}
                                            </td>
                                            <td className="table-list-cell"><span className="cell-code">{p.template_name || p.template_code || '—'}</span></td>
                                            <td className="table-list-cell">{p.location_name || '—'}</td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-xs text-gray-600">
                                                    <Calendar size={12} className="text-gray-400" />
                                                    {formatDate(p.start_date)} → {formatDate(p.end_date)}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-sm">
                                                    <User size={12} className="text-gray-400" />
                                                    {p.maker_name || '—'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-sm">
                                                    <User size={12} className="text-gray-400" />
                                                    {p.checker_name || '—'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell" style={{ textAlign: 'right' }}>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(p.status)}`}>
                                                    {p.status || 'Not-Started'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={filtered.length}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="programs"
                        />
                    </div>
                </div>
            </div>

            <RATRolloutModal
                isOpen={isRolloutOpen}
                onClose={() => setIsRolloutOpen(false)}
                onRolledOut={() => {
                    setToast({ type: 'success', message: 'RAT Program rolled out.' });
                    refresh();
                }}
                onError={(msg) => setToast({ type: 'error', message: msg })}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />

            {openId !== null && (
                <RiskAssessmentExecutionModal
                    programId={openId}
                    readOnly
                    onClose={() => setOpenId(null)}
                />
            )}

            <WorkflowModal
                isOpen={!!workflowRow}
                onClose={() => setWorkflowRow(null)}
                kind={WF_KIND}
                id={workflowRow?.id ?? null}
                onChanged={refresh}
            />
        </div>
    );
};

export default RiskPrograms;
